import React, { useState } from "react";
import { Helmet } from 'react-helmet-async';
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import ScrollReveal from "../components/ui/ScrollReveal";
import "../App.css";
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { sendContactMessage } from '../services/emailService';

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (!formData.email.trim()) newErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.email))
            newErrors.email = "Invalid email format";
        if (!formData.subject.trim()) newErrors.subject = "Subject is required";
        if (!formData.message.trim()) newErrors.message = "Message is required";
        else if (formData.message.length < 10)
            newErrors.message = "Message must be at least 10 characters";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);
        setSuccessMessage("");

        try {
            const newMessage = {
                id: Date.now().toString(),
                type: 'contact',
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                subject: formData.subject,
                message: formData.message,
                read: false,
                created_at: new Date().toISOString()
            };

            // Save to Firestore (primary)
            let firestoreSaved = false;
            try {
                const docRef = await addDoc(collection(db, 'messages'), newMessage);
                console.log('✅ Message saved to Firestore with ID:', docRef.id);
                firestoreSaved = true;
            } catch (firestoreError) {
                console.error('❌ Firestore write failed:', firestoreError.message);
            }

            // Only save to localStorage if Firestore failed (fallback)
            if (!firestoreSaved) {
                const MESSAGES_KEY = 'siteMessages';
                const existingMessages = JSON.parse(localStorage.getItem(MESSAGES_KEY) || '[]');
                existingMessages.unshift(newMessage);
                localStorage.setItem(MESSAGES_KEY, JSON.stringify(existingMessages));
            }

            // Send email to info@pridepath.lk
            try {
                await sendContactMessage(formData);
                console.log('✅ Email sent to info@pridepath.lk');
            } catch (emailError) {
                console.warn('⚠️ Email sending failed:', emailError);
                // Don't fail the submission if email fails
            }

            // Dispatch event for real-time notification in admin dashboard
            window.dispatchEvent(new CustomEvent('newMessage', { detail: newMessage }));

            // Success
            setSuccessMessage("Message sent successfully! We'll reply soon.");
            setFormData({
                name: "",
                email: "",
                phone: "",
                subject: "",
                message: "",
            });
        } catch (error) {
            setErrors({ form: "Failed to send message. Please try again." });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <Helmet>
                <title>Contact Us | Pride Path Commercial - Buttala Head Office</title>
                <meta name="description" content="Contact Pride Path Commercial in Buttala, Sri Lanka. Call +94 55 313 0227, email pridepathlk@gmail.com, or visit our head office at 11 Mile Post, Monaragala Road." />
                <meta property="og:title" content="Contact Pride Path Commercial" />
                <meta property="og:description" content="Get in touch with our team for leasing, loans, and financial services." />
            </Helmet>
            <Header />

            {/* Hero Section with Background Image */}
            <section className="relative h-[100svh] min-h-[550px] md:min-h-[650px] overflow-hidden">
                {/* Background Image - High Quality */}
                <div
                    className="absolute inset-0 bg-cover bg-center brightness-150 contrast-110 saturate-110"
                    style={{
                        backgroundImage: "url('https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=2400&h=1600&fit=crop&q=100')",
                    }}
                />
                {/* Lighter Gradient Overlay for better visibility */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-black/15" />
                <div
                    className="absolute inset-0"
                    style={{
                        background: "linear-gradient(135deg, rgba(13, 148, 136, 0.65) 0%, rgba(20, 184, 166, 0.55) 50%, rgba(15, 118, 110, 0.65) 100%)",
                    }}
                />
                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-center items-center px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        {/* Breadcrumb */}
                        <nav className="flex items-center justify-center text-sm text-white/70 mb-6">
                            <a href="/" className="hover:text-white transition-colors">Home</a>
                            <svg className="w-4 h-4 mx-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <span className="text-white">Contact Us</span>
                        </nav>
                        {/* Title */}
                        <h1
                            className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4"
                            style={{ textShadow: "2px 2px 8px rgba(0,0,0,0.4)" }}
                        >
                            Get In Touch
                        </h1>
                        <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
                            Have questions? We'd love to hear from you. Send us a message and
                            we'll respond as soon as possible.
                        </p>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section className="max-w-6xl mx-auto px-4 py-16 -mt-10 relative z-10">
                <div
                    className="bg-white rounded-2xl overflow-hidden"
                    style={{ boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                >
                    <div className="grid lg:grid-cols-5">
                        {/* Contact Info - Left Side */}
                        <div
                            className="lg:col-span-2 p-8 lg:p-12 text-white"
                            style={{
                                background: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)",
                            }}
                        >
                            <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
                            <p className="text-teal-100 mb-8">
                                Fill out the form and our team will get back to you within 24
                                hours.
                            </p>

                            <div className="space-y-6">
                                {/* Address */}
                                <div className="flex items-start space-x-4 group cursor-pointer transform transition-all duration-300 hover:translate-x-2">
                                    <div
                                        className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                                        style={{
                                            background: "linear-gradient(135deg, #f59e0b, #f97316)",
                                            boxShadow: "0 4px 15px rgba(245, 158, 11, 0.4)"
                                        }}
                                    >
                                        <svg
                                            className="w-6 h-6 text-white animate-bounce"
                                            style={{ animationDuration: "2s" }}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                            />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                            />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-lg">Address</h3>
                                        <p className="text-teal-100 text-sm mt-1">
                                            11 Mile Post, Monaragala Road
                                            <br />
                                            Buttala
                                        </p>
                                    </div>
                                </div>

                                {/* Phone - Clickable */}
                                <a href="tel:+94553130227" className="flex items-start space-x-4 group cursor-pointer transform transition-all duration-300 hover:translate-x-2">
                                    <div
                                        className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                                        style={{
                                            background: "linear-gradient(135deg, #10b981, #14b8a6)",
                                            boxShadow: "0 4px 15px rgba(16, 185, 129, 0.4)"
                                        }}
                                    >
                                        <svg
                                            className="w-6 h-6 text-white"
                                            style={{ animation: "pulse 2s infinite" }}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                            />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-lg">Phone</h3>
                                        <p className="text-teal-100 text-sm mt-1 group-hover:text-white group-hover:underline transition-colors">
                                            +94 55 313 0227
                                        </p>
                                        <span className="text-xs text-teal-200 opacity-0 group-hover:opacity-100 transition-opacity">Click to call</span>
                                    </div>
                                </a>

                                {/* Email - Clickable */}
                                <a href="mailto:pridepathlk@gmail.com" className="flex items-start space-x-4 group cursor-pointer transform transition-all duration-300 hover:translate-x-2">
                                    <div
                                        className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                                        style={{
                                            background: "linear-gradient(135deg, #8b5cf6, #a855f7)",
                                            boxShadow: "0 4px 15px rgba(139, 92, 246, 0.4)"
                                        }}
                                    >
                                        <svg
                                            className="w-6 h-6 text-white"
                                            style={{ animation: "pulse 2s infinite", animationDelay: "0.5s" }}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                            />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-lg">Email</h3>
                                        <p className="text-teal-100 text-sm mt-1 group-hover:text-white group-hover:underline transition-colors">
                                            pridepathlk@gmail.com
                                        </p>
                                        <span className="text-xs text-teal-200 opacity-0 group-hover:opacity-100 transition-opacity">Click to send email</span>
                                    </div>
                                </a>

                                {/* Hours */}
                                <div className="flex items-start space-x-4 group cursor-pointer transform transition-all duration-300 hover:translate-x-2">
                                    <div
                                        className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                                        style={{
                                            background: "linear-gradient(135deg, #3b82f6, #6366f1)",
                                            boxShadow: "0 4px 15px rgba(59, 130, 246, 0.4)"
                                        }}
                                    >
                                        <svg
                                            className="w-6 h-6 text-white"
                                            style={{ animation: "spin 8s linear infinite" }}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-lg">Business Hours</h3>
                                        <p className="text-teal-100 text-sm mt-1">
                                            Mon - Fri : 8:30 AM - 5:00 PM
                                            <br />
                                            Sat : 8:30 AM - 1:00 PM
                                            <br />
                                            Sunday and other Mercantile Holidays : Closed
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contact Form - Right Side */}
                        <div className="lg:col-span-3 p-8 lg:p-12">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">
                                Send us a message
                            </h2>

                            {successMessage && (
                                <div
                                    className="mb-6 p-4 rounded-lg"
                                    style={{ backgroundColor: "#dcfce7", color: "#166534" }}
                                >
                                    {successMessage}
                                </div>
                            )}

                            {errors.form && (
                                <div
                                    className="mb-6 p-4 rounded-lg"
                                    style={{ backgroundColor: "#fee2e2", color: "#991b1b" }}
                                >
                                    {errors.form}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid md:grid-cols-2 gap-6">
                                    {/* Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Full Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                                            style={{
                                                borderColor: errors.name ? "#ef4444" : "#d1d5db",
                                            }}
                                            placeholder="John Doe"
                                        />
                                        {errors.name && (
                                            <p className="mt-1 text-sm" style={{ color: "#ef4444" }}>
                                                {errors.name}
                                            </p>
                                        )}
                                    </div>

                                    {/* Email */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Email Address *
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                                            style={{
                                                borderColor: errors.email ? "#ef4444" : "#d1d5db",
                                            }}
                                            placeholder="john@example.com"
                                        />
                                        {errors.email && (
                                            <p className="mt-1 text-sm" style={{ color: "#ef4444" }}>
                                                {errors.email}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Phone */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Phone Number{" "}
                                        <span className="text-gray-400">(optional)</span>
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                                        style={{ borderColor: "#d1d5db" }}
                                        placeholder="+1 (555) 000-0000"
                                    />
                                </div>

                                {/* Subject */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Subject *
                                    </label>
                                    <input
                                        type="text"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                                        style={{
                                            borderColor: errors.subject ? "#ef4444" : "#d1d5db",
                                        }}
                                        placeholder="How can we help you?"
                                    />
                                    {errors.subject && (
                                        <p className="mt-1 text-sm" style={{ color: "#ef4444" }}>
                                            {errors.subject}
                                        </p>
                                    )}
                                </div>

                                {/* Message */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Message *
                                    </label>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        rows={5}
                                        className="w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-teal-500 focus:border-transparent transition resize-none"
                                        style={{
                                            borderColor: errors.message ? "#ef4444" : "#d1d5db",
                                        }}
                                        placeholder="Tell us more about your inquiry..."
                                    />
                                    {errors.message && (
                                        <p className="mt-1 text-sm" style={{ color: "#ef4444" }}>
                                            {errors.message}
                                        </p>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full md:w-auto px-8 py-4 text-white font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2"
                                    style={{
                                        background: isSubmitting
                                            ? "#9ca3af"
                                            : "linear-gradient(135deg, #0d9488, #14b8a6)",
                                    }}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <svg
                                                className="animate-spin h-5 w-5 text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                ></path>
                                            </svg>
                                            <span>Sending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                                                />
                                            </svg>
                                            <span>Send Message</span>
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ContactPage;
